import { FC, useCallback, useEffect, useRef } from 'react';
import HanziWriter, { StrokeData } from 'hanzi-writer';
import { TracingDifficulty, STROKE_ACCURACY_THRESHOLD, MEDIUM_OUTLINE_FADE_MS } from '../utils';
import * as S from './styles';

interface Props {
  character: string;
  difficulty: TracingDifficulty;
  onComplete: (accuracy: number) => void;
  disabled: boolean;
}

export const HanziWriterTracer: FC<Props> = ({ character, difficulty, onComplete, disabled }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const writerRef = useRef<HanziWriter | null>(null);
  const totalStrokesRef = useRef(0);
  const correctStrokesRef = useRef(0);

  const getOutlineVisibility = useCallback(() => {
    switch (difficulty) {
      case 'easy':
        return { showOutline: true, showCharacter: false };
      case 'medium':
        return { showOutline: true, showCharacter: false };
      case 'hard':
        return { showOutline: false, showCharacter: false };
    }
  }, [difficulty]);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    container.innerHTML = '';
    totalStrokesRef.current = 0;
    correctStrokesRef.current = 0;

    const { showOutline, showCharacter } = getOutlineVisibility();

    const writer = HanziWriter.create(container, character, {
      width: 280,
      height: 280,
      padding: 20,
      showOutline,
      showCharacter,
      strokeColor: '#333333',
      outlineColor: '#cccccc',
      drawingColor: '#333333',
      showHintAfterMisses: 3,
      highlightOnComplete: true,
      strokeAnimationSpeed: 1,
      delayBetweenStrokes: 100,
    });

    writerRef.current = writer;

    if (difficulty === 'medium') {
      setTimeout(() => {
        writer.hideOutline({ duration: 500 });
      }, MEDIUM_OUTLINE_FADE_MS);
    }

    writer.quiz({
      onMistake: (strokeData: StrokeData) => {
        totalStrokesRef.current += 1;
        if (strokeData.mistakesOnStroke === 0) {
          correctStrokesRef.current += 1;
        }
      },
      onCorrectStroke: () => {
        totalStrokesRef.current += 1;
        correctStrokesRef.current += 1;
      },
      onComplete: (summaryData: { character: string; totalMistakes: number }) => {
        const totalMistakes = summaryData.totalMistakes;
        const accuracy = totalMistakes === 0 ? 1 : 1 / (1 + totalMistakes);
        onComplete(Math.min(accuracy, 1));
      },
      leniency: STROKE_ACCURACY_THRESHOLD,
    });

    return () => {
      container.innerHTML = '';
      writerRef.current = null;
    };
  }, [character, difficulty, getOutlineVisibility, onComplete]);

  useEffect(() => {
    if (disabled && writerRef.current) {
      writerRef.current.cancelQuiz();
    }
  }, [disabled]);

  return <S.Container ref={containerRef} data-testid="hanzi-writer-tracer" />;
};
